import { StudiesLayout } from "@/components/layouts";
import { Button } from "@/components/ui/button";
import { DragDropUpload, useUploadsStore } from "@/components/ui/drag-drop-upload";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useEffect } from "react";

/* Prop Types */
interface StudyAssetContextPackSelectRouteProps {
  projectId: string;
  studyId: string;
  contextPackId: string;
}

/*
 * StudyAssetContextPackSelectRoute Component
 * Here the user stages the assets they want to upload to their context pack inside the study flow
 */
export const StudyAssetContextPackSelectRoute = ({ projectId, studyId, contextPackId }: StudyAssetContextPackSelectRouteProps) => {
  /* Hooks */
  const { navigateTo } = useStudyNavigation(projectId, studyId, "study-context");

  /* Stores */
  const uploads = useUploadsStore((state) => state.uploads);
  const { reset } = useUploadsStore((state) => state.uploadActions);

  /* Side effects */
  useEffect(() => {
    // Reset the uploads store when the component mounts as we are starting a new upload process
    reset();
  }, [reset]);

  /* Handlers */
  const handleUpload = () => {
    navigateTo(`/projects/${projectId}/studies/${studyId}/asset/study-context/${contextPackId}/upload`);
  };

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="study-context">
      {/* Primary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Upload contexts</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" disabled={!uploads.length || !uploads.every((upload) => upload.isValid)} onClick={handleUpload}>
            Next: add dropzones <ChevronRightIcon className="size-3.5" />
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Secondary Toolbar */}
      <Toolbar>
        <ToolbarItem>
          <div>
            <p className="text-base-content/70">This is where you upload photos or digital renders of the context where your asset would be placed.</p>
            <p className="text-base-content/70">Supported file types: JPG, JPEG and PNG. High resolution uploads are scaled down for performance optimisation.</p>
          </div>
        </ToolbarItem>
      </Toolbar>

      {/* Drag & Drop */}
      <DragDropUpload
        dragDropText="or select context images from your computer"
        dragDropVideoUrl="/videos/upload-animation.webm"
        segmentTrackName="Context Pack"
        segmentTrackMediaType="Image"
        additionalSegmentProperties={{ studyId, projectId }}
        selectAssetsText="Select contexts"
      />
    </StudiesLayout>
  );
};
